import { z } from 'zod';
// ============================================================
// Payment Schemas
// ============================================================
/** 상품 코드(=결제 타입) 포맷. 대문자 영문으로 시작, 대문자·숫자·밑줄 3~64자. */
export const PRODUCT_CODE_REGEX = /^[A-Z][A-Z0-9_]{2,63}$/;
export const PRODUCT_CODE_MESSAGE = '상품 코드는 대문자 영문으로 시작하는 대문자·숫자·밑줄 3~64자여야 합니다';
/**
 * 결제 타입 = products.code (FIKET-315). 과거 enum(payment_type) 값은 전부 이 포맷을 만족한다.
 * 존재 여부(실제 상품인지)는 API가 products 테이블로 판정한다 — 스키마는 포맷만 본다.
 */
export const PaymentTypeSchema = z.string().regex(PRODUCT_CODE_REGEX, PRODUCT_CODE_MESSAGE);
/** @deprecated 0097 이전 payment_type enum 값. 시딩·마이그레이션 검증 참고용. 신규 코드에서 사용 금지 */
export const LEGACY_PAYMENT_TYPE_VALUES = [
    'PREMIUM_UPGRADE', 'AI_CREDITS_BUNDLE', 'CREDITS_10', 'CREDITS_20', 'CREDITS_30', 'CREDITS_50', 'CREDITS_100',
    'AI_CREDITS', 'INVITATION_PACKAGE', 'INVITATION_MAIN',
    'STUDIO_PHOTOS_10', 'STUDIO_PHOTOS_20', 'STUDIO_PHOTOS_30',
    'SNAP_MAIN', 'SNAP_PHOTOS_10', 'SNAP_PHOTOS_20',
    'THANKYOU_ADDON', 'ALL_PACKAGE', 'THANKS_PACKAGE',
];
export const PaymentStatusSchema = z.enum(['PENDING', 'COMPLETED', 'FAILED', 'REFUNDED']);
export const PaymentMethodSchema = z.enum(['TOSS', 'KAKAO_PAY', 'CARD', 'NAVER_PAY']);
export const PaymentChannelSchema = z.enum(['SITE', 'SMARTSTORE']);
export const PaymentSchema = z.object({
    id: z.string().cuid2(),
    userId: z.string().cuid2(),
    type: PaymentTypeSchema,
    method: PaymentMethodSchema,
    amount: z.number().int().min(0),
    creditsGranted: z.number().int().min(0).nullable(),
    status: PaymentStatusSchema.default('PENDING'),
    orderId: z.string().max(255).nullable(),
    paymentKey: z.string().max(255).nullable(),
    createdAt: z.date(),
});
export const CreatePaymentRequestSchema = z.object({
    type: PaymentTypeSchema,
    method: PaymentMethodSchema.default('TOSS'),
});
export const ApprovePaymentRequestSchema = z.object({
    orderId: z.string().min(1, '주문 ID가 필요합니다'),
    paymentKey: z.string().min(1, '결제 키가 필요합니다'),
    amount: z.number().int().min(0, '결제 금액이 필요합니다'),
});
export const PaymentResponseSchema = PaymentSchema.omit({
    userId: true,
    paymentKey: true,
});
export const PaymentListResponseSchema = z.object({
    payments: z.array(PaymentResponseSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1).max(100),
});
// ============================================================
// 환불 사유 (FIKET-226 — 토스 셀프 환불 시 사유 선택)
// ============================================================
/** 환불 사유 프리셋 코드 — refund_events.reason(varchar 32)에 저장 */
export const REFUND_REASON_VALUES = [
    'CHANGE_OF_MIND',
    'PHOTO_QUALITY',
    'HARD_TO_USE',
    'PRICE',
    'OTHER',
];
export const RefundReasonSchema = z.enum(REFUND_REASON_VALUES);
/** 환불 사유 프리셋 — 코드와 화면 라벨. web·api 공용 */
export const REFUND_REASONS = [
    { value: 'CHANGE_OF_MIND', label: '단순 변심' },
    { value: 'PHOTO_QUALITY', label: '사진 품질 불만족' },
    { value: 'HARD_TO_USE', label: '사용이 어려움' },
    { value: 'PRICE', label: '가격 불만' },
    { value: 'OTHER', label: '기타' },
];
/** POST /api/payments/:paymentId/cancel 요청 body — 사유는 선택(기존 호출처 하위호환) */
export const CancelPaymentRequestSchema = z.object({
    reason: RefundReasonSchema.optional(),
    // '기타' 선택 시 직접 입력한 상세 사유
    reasonDetail: z.string().trim().max(500).optional(),
});
// ============================================================
// Smartstore
// ============================================================
export const ActivateSmartStoreRequestSchema = z.object({
    orderId: z.string().min(1, '주문번호를 입력하세요'),
});
// ============================================================
// Activation Codes
// ============================================================
export const ActivationCodeStatusSchema = z.enum(['AVAILABLE', 'USED', 'EXPIRED']);
export const ActivateCodeRequestSchema = z.object({
    code: z
        .string()
        .regex(/^FIKT-[A-Z2-9]{4}-[A-Z2-9]{4}$/, '올바른 활성화 코드 형식이 아닙니다 (FIKT-XXXX-XXXX)'),
});
// ============================================================
// 토스 결제
// ============================================================
// 결제 시점 유입 정보 — 구매 브라우저 세션의 first-touch(utm/referrer/landing).
// 클라이언트 sessionStorage(fw_attribution)에서 읽어 /toss/ready에 전달. 전부 optional.
// max는 클라이언트 slice 한도(1024)보다 여유 있게 — 초과분은 서버가 컬럼 한도로 자른다.
export const PaymentAttributionSchema = z.object({
    utmSource: z.string().max(1100).optional(),
    utmMedium: z.string().max(1100).optional(),
    utmCampaign: z.string().max(1100).optional(),
    utmTerm: z.string().max(1100).optional(),
    utmContent: z.string().max(1100).optional(),
    referrer: z.string().max(2048).optional(),
    landingPath: z.string().max(2048).optional(),
});
/** 시크릿 코드 입력 — trim 후 대문자 정규화. 판정은 서버(프로모션 엔진). */
const PromoCodeInputSchema = z
    .string()
    .trim()
    .transform((s) => s.toUpperCase())
    .pipe(z.string().regex(/^[A-Z0-9]{4,20}$/, '코드는 영문·숫자 4~20자입니다'));
export const TossReadyRequestSchema = z
    .object({
    productType: PaymentTypeSchema,
    // 구매세션 ID — 재시도(새 orderId)를 같은 구매 흐름으로 묶는 클라이언트 발급 키(cuid2).
    // 구버전 클라이언트 하위호환을 위해 optional.
    purchaseSessionId: z.string().min(1).max(128).optional(),
    // 결제 시점 유입 — 구버전 클라이언트 하위호환을 위해 optional.
    attribution: PaymentAttributionSchema.optional(),
    /** 시크릿 코드 프로모션(CODE). couponId 와 동시 불가 */
    promoCode: PromoCodeInputSchema.optional(),
    /** 이벤트 쿠폰(EVENT) — 릴리스 C. 스키마 자리만 */
    couponId: z.string().min(1).max(128).optional(),
})
    .refine((v) => !(v.promoCode && v.couponId), {
    message: '코드와 쿠폰은 하나만 적용할 수 있습니다',
    path: ['promoCode'],
});
export const TossConfirmRequestSchema = z.object({
    orderId: z.string().min(1, '주문 ID가 필요합니다'),
    paymentKey: z.string().min(1, '결제 키가 필요합니다'),
    amount: z.number().int().min(1, '결제 금액이 필요합니다'),
});
/**
 * 토스가 "사용자 취소/미진행"으로 넘겨주는 에러 코드 — 실제 결제 실패(승인 거절)가 아니라
 * 이탈로 분류한다. 서버(abort 사유 기록)와 웹(취소 토스트 억제)이 같은 집합을 써야 한다.
 *
 * - PAY_PROCESS_CANCELED: failUrl 리다이렉트(모바일 self 창)에서 사용자가 취소
 * - PAY_PROCESS_ABORTED:  failUrl — "결제가 취소되었거나 진행되지 않았습니다"
 * - USER_CANCEL:          SDK v2 Promise 거절(PC iframe 창 닫기, 메시지 "취소되었습니다.")
 *
 * FIKET-413: USER_CANCEL이 빠져 PC 결제창 닫기가 PG_DECLINED(승인 거절)로 기록되던 오분류 수정.
 */
export const TOSS_USER_CANCEL_CODES = ['PAY_PROCESS_CANCELED', 'PAY_PROCESS_ABORTED', 'USER_CANCEL'];
export function isTossUserCancelCode(code) {
    return !!code && TOSS_USER_CANCEL_CODES.includes(code);
}
export const TossAbortRequestSchema = z.object({
    orderId: z.string().min(1, '주문 ID가 필요합니다'),
    // 토스 결제창/실패 리다이렉트가 넘겨준 에러 코드·메시지 — 실패 사유 분류용(선택).
    // TOSS_USER_CANCEL_CODES(또는 미전달)=사용자 취소, 그 외=실제 결제 실패로 기록된다.
    failCode: z.string().max(64).optional(),
    failMessage: z.string().max(500).optional(),
});
/**
 * 셀프 환불 가능 여부 — 새 selfRefundable 필드 우선, 없으면 activatesAccount에서 SAFE 파생.
 * product 미발견 또는 둘 다 미지정 → false(환불 차단)가 안전 기본값.
 * (참고: 이 product 축은 grant.ts의 LotPolicy.refundable(크레딧 lot 축)과 다른 개념)
 */
export function getProductSelfRefundable(p) {
    if (!p)
        return false;
    if (typeof p.selfRefundable === 'boolean')
        return p.selfRefundable;
    // 구 row 파생: activatesAccount=true → 환불불가. 둘 다 미지정이면 false(보수적 차단).
    if (typeof p.activatesAccount === 'boolean')
        return !p.activatesAccount;
    return false;
}
/**
 * 활성화 슬롯을 지급하는 상품인지 여부 (범용/청첩장/감사장 중 하나라도 > 0).
 * 슬롯 상품은 환불 가부·사유를 크레딧이 아니라 "슬롯 소비 여부"로 판정해야 하므로,
 * 환불 자격 판정(evaluateRefundEligibility)에서 슬롯 브랜치 진입 조건으로 쓴다.
 * (참고: selfRefundable(환불 차단 축)·LotPolicy.refundable(크레딧 lot 축)과 별개 개념)
 */
export function getProductGrantsSlots(p) {
    if (!p)
        return false;
    return (p.universalSlots ?? 0) + (p.invitationSlots ?? 0) + (p.thankyouSlots ?? 0) > 0;
}
/**
 * 구매 페이지 분류 — 새 category 필드 우선, 없으면 type/activatesAccount에서 파생.
 */
export function getProductCategory(p) {
    if (p.category)
        return p.category;
    // wedding 탭: 청첩장·올인원(activatesAccount) + 감사장 상품
    if (p.activatesAccount)
        return 'wedding';
    if (p.type === 'THANKS_PACKAGE' || p.type === 'THANKYOU_ADDON')
        return 'wedding';
    // studio 탭: AI 스튜디오·스냅
    if (p.type.startsWith('STUDIO_'))
        return 'studio';
    if (p.type.startsWith('SNAP_'))
        return 'studio';
    // 분류 미지정 + 알 수 없는 type(레거시 등)도 구매 페이지에서 사라지지 않도록 studio 탭으로 기본 노출
    return 'studio';
}
/**
 * 구매 페이지 카드 체크 항목(features) — 카탈로그 features 우선, 비면 type별 기본 문구 fallback.
 * 카탈로그 features는 운영자가 적은 문구 그대로, fallback만 credits를 보간한다.
 */
export function getProductFeatures(p) {
    if (p.features && p.features.length > 0)
        return p.features;
    if (p.type === 'THANKS_PACKAGE') {
        return ['감사장 2장 제작 가능', '다양한 디자인 템플릿', '카카오톡·문자로 간편 공유'];
    }
    if (p.type === 'ALL_PACKAGE') {
        return [
            '청첩장 + 감사장 모두 포함',
            `AI 스튜디오 사용권 ${p.credits}개 포함`,
            '워터마크 제거 · 전체 템플릿',
            '참석여부 · 방명록',
        ];
    }
    if (!getProductSelfRefundable(p)) {
        return [
            `AI 스튜디오 사용권 ${p.credits}개 포함`,
            '워터마크 제거',
            '전체 템플릿 사용',
            '참석여부 · 방명록',
        ];
    }
    return [`AI 사진 ${p.credits}장 생성`];
}
// 스마트스토어 7개 옵션과 1:1 매칭. 가격은 스마트스토어 노출가 기준 (2026-05-11 동기화).
// 운영 중 가격 변경 시 admin > 앱 설정의 app_settings.product_catalog 업데이트.
export const DEFAULT_PRODUCT_CATALOG = [
    {
        // 청첩장 패키지 — 스마트스토어 단일상품(sellerProductCode=INVITATION_PACKAGE) & 사이트 청첩장.
        // 활성화 + 범용 활성화 슬롯 3. AI 사진 사용권은 포함하지 않는다(prod 카탈로그 실값 credits 0, 2026-09-21 정리).
        // 실제 상품 구성은 app_settings.product_catalog 가 이 기본값을 덮어쓴다 — 여기 값은 폴백일 뿐.
        type: 'INVITATION_PACKAGE',
        nameKo: '모바일 청첩장',
        description: '나만의 모바일 청첩장 제작',
        credits: 0,
        price: 11200,
        basePrice: 16000,
        discountPercent: 30,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: true,
        requiresActivation: false,
        universalSlots: 3,
    },
    {
        type: 'STUDIO_PHOTOS_10',
        nameKo: 'AI 스튜디오 사진 10장',
        credits: 10,
        price: 15000,
        basePrice: 15000,
        discountPercent: 0,
        // SITE(토스) 채널에서 노출 제외 — 스튜디오 상품은 스마트스토어에서만 판매
        channels: ['SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        type: 'STUDIO_PHOTOS_20',
        nameKo: 'AI 스튜디오 사진 20장',
        credits: 20,
        price: 25000,
        basePrice: 25000,
        discountPercent: 0,
        channels: ['SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        type: 'STUDIO_PHOTOS_30',
        nameKo: 'AI 스튜디오 사진 30장',
        credits: 30,
        price: 30000,
        basePrice: 30000,
        discountPercent: 0,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        type: 'SNAP_MAIN',
        nameKo: 'AI 스냅 메인 (10장)',
        credits: 10,
        price: 15000,
        basePrice: 20000,
        discountPercent: 25,
        // SITE 노출 제외 — 스냅 메인은 스마트스토어 전용으로 운영
        channels: ['SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        type: 'SNAP_PHOTOS_10',
        nameKo: 'AI 스냅 10장',
        credits: 10,
        price: 15000,
        basePrice: 15000,
        discountPercent: 0,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        type: 'SNAP_PHOTOS_20',
        nameKo: 'AI 스냅 20장',
        credits: 20,
        price: 25000,
        basePrice: 25000,
        discountPercent: 0,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
    },
    {
        // 감사장 상품 — 감사장 활성화 슬롯 2개 부여 (감사장 전용). 생성 한도는 안 올림.
        // 사이트 판매는 THANKS_PACKAGE 하나로 정리 — THANKYOU_ADDON은 기능 중복(슬롯2) placeholder라
        // SITE 채널 제외(스마트스토어 전용으로만 잔존). PurchasePage에는 노출되지 않는다.
        type: 'THANKYOU_ADDON',
        nameKo: '감사장 활성화',
        // 가격은 운영 확정 후 admin app_settings.product_catalog에서 갱신. 아래는 placeholder.
        credits: 0,
        price: 5000,
        basePrice: 5000,
        discountPercent: 0,
        channels: ['SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
        thankyouSlots: 2,
    },
    {
        // 웨딩 올인원 패키지 — 청첩장 전용 활성화 슬롯 3 + 감사장 전용 슬롯 2 + AI 크레딧 20
        type: 'ALL_PACKAGE',
        nameKo: '웨딩 올인원 패키지',
        description: '청첩장 + 감사장 + AI 스튜디오 올인원',
        credits: 20,
        price: 24910,
        basePrice: 47000,
        discountPercent: 47,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: true,
        requiresActivation: false,
        invitationSlots: 3,
        thankyouSlots: 2,
        promoBadge: 'AI 스냅 10장 추가 혜택',
    },
    {
        // 감사장 패키지 — 감사장 활성화 슬롯 2개 (크레딧 없음)
        type: 'THANKS_PACKAGE',
        nameKo: '감사장 패키지',
        description: '감사한 마음을 전하는 모바일 감사장',
        credits: 0,
        price: 7480,
        basePrice: 11000,
        discountPercent: 32,
        channels: ['SITE', 'SMARTSTORE'],
        activatesAccount: false,
        requiresActivation: true,
        thankyouSlots: 2,
    },
];
// ============================================================
// 구매 보너스 크레딧 프로모션
// ============================================================
// 기간 보너스 설정(credit_promotions JSON)은 promotions 테이블(kind = AUTO)로 이관됨 — schemas/promotion.ts 참조
/** 구매/활성화에 적용된 프로모션 1건의 결과 (API 응답·UI 공용) */
export const BonusPromotionSchema = z.object({
    name: z.string(),
    bonusCredits: z.number().int(),
});
/**
 * 결제 금액 내역 스냅샷 — 결제 성공 화면 "결제 금액" 블록용.
 * 카탈로그 할인(정가 → 판매가)과 코드/쿠폰 할인(판매가 → 실결제)을 구분해 "할인에 할인"을 보여준다.
 * basePrice·catalogDiscountPercent 는 confirm 시점 카탈로그 값, listAmount·promotionDiscountAmount·amount 는 결제 행 스냅샷.
 */
export const PaymentPricingSchema = z.object({
    /** 카탈로그 정가 */
    basePrice: z.number().int(),
    /** 카탈로그 할인율(%) — 0 이면 카탈로그 할인 없음 */
    catalogDiscountPercent: z.number().int(),
    /** 정가 − 판매가 (0 이상) */
    catalogDiscountAmount: z.number().int(),
    /** 판매가 — 코드/쿠폰 할인의 기준값 (payments.list_amount) */
    listAmount: z.number().int(),
    /** 코드/쿠폰 할인액 (payments.discount_amount, 없으면 0) */
    promotionDiscountAmount: z.number().int(),
    /** 실결제 금액 (payments.amount) */
    amount: z.number().int(),
});
// ============================================================
// Constants (deprecated — 기존 코드 호환용)
// ============================================================
/** @deprecated DEFAULT_PRODUCT_CATALOG 사용 */
export const PAYMENT_AMOUNTS = {
    PREMIUM_UPGRADE: 9900,
    AI_CREDITS: 1000,
    AI_CREDITS_BUNDLE: 8000,
};
/** @deprecated DEFAULT_PRODUCT_CATALOG 사용 */
export const CREDITS_GRANTED = {
    PREMIUM_UPGRADE: 10,
    AI_CREDITS: 1,
    AI_CREDITS_BUNDLE: 10,
};
export const validatePaymentAmount = (type, amount) => {
    // 새 상품 타입은 카탈로그에서 검증
    const product = DEFAULT_PRODUCT_CATALOG.find((p) => p.type === type);
    if (product)
        return product.price === amount;
    // 레거시 타입
    const legacy = PAYMENT_AMOUNTS[type];
    return legacy === amount;
};
export const calculateCredits = (type) => {
    // 새 상품 타입은 카탈로그에서 조회
    const product = DEFAULT_PRODUCT_CATALOG.find((p) => p.type === type);
    if (product)
        return product.credits;
    // 레거시 타입
    return CREDITS_GRANTED[type] ?? 0;
};
// ============================================================
// 스마트스토어 자동 활성화
// ============================================================
export const CheckUnactivatedResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        activated: z.array(z.object({
            productOrderId: z.string(),
            productName: z.string(),
            creditsGranted: z.number().int(),
            activationSlotsGranted: z.number().int(),
        })),
    }),
});
export const generateOrderId = (userId, type) => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `${type}_${userId.slice(0, 8)}_${timestamp}_${random}`;
};
// ============================================================
// 참조사진 품질 안내 + 1회 크레딧 복구 (환불 모달)
// ============================================================
/**
 * app_settings 키 — 환불 모달에서 "참조사진 품질 경고를 안내받고도 생성한 결제"에
 * 위로의 뜻으로 한 번 돌려주는 크레딧 수. 0이면 안내만 보여주고 복구 버튼은 숨긴다.
 */
export const REFUND_QUALITY_RECOVERY_SETTING_KEY = 'refund_quality_recovery';
export const RefundQualityRecoverySettingSchema = z.object({
    credits: z.number().int().min(0).max(100),
});
/** 기본값 0 = 안내만. 운영에서 어드민(참조사진 품질 페이지)으로 켠다 — 마이그레이션 없음 */
export const DEFAULT_REFUND_QUALITY_RECOVERY = { credits: 0 };
/**
 * refund-info 응답의 품질 안내 블록. 이 결제의 크레딧이 warn 판정 참조사진으로 생성에 쓰였을 때만 내려온다(아니면 null).
 * 환불 가능 여부·금액에는 영향이 없다 — 안내와 1회 복구 제안만.
 */
export const RefundQualityNoticeSchema = z.object({
    /** warn 참조사진을 쓴 생성 잡 수 */
    warnedJobCount: z.number().int().min(0),
    /** 그 잡들이 이 결제에서 소비한 크레딧 합(부분 실패 환급 차감 후) */
    warnedCredits: z.number().int().min(0),
    /** 업로드 당시 안내한 경고 사유 라벨(중복 제거, 한국어) */
    warningLabels: z.array(z.string()),
    /** 설정된 복구 크레딧 수(0이면 버튼 없음) */
    recoveryCredits: z.number().int().min(0),
    /** 지금 이 사용자가 복구를 받을 수 있는가 */
    recoveryAvailable: z.boolean(),
    /** 계정 생애 1회를 이미 수령했는가 */
    recoveryClaimed: z.boolean(),
});
/** POST /api/payments/:paymentId/quality-recovery 응답 data */
export const QualityRecoveryResponseSchema = z.object({
    creditsGranted: z.number().int().min(1),
    newBalance: z.number().int().min(0),
});
//# sourceMappingURL=payment.js.map